import { ForeAftEnum } from "open-vessel-definition";
import { feetToMillimeters, pad3 } from "@tedivo/tedivo-pure-helpers";
/**
 * Given a set of LCGs of different sizes, find the LCG for every 20 bay
 * @param sizeSummary
 * @param bayLevelSizesAndCgsAbove
 * @param bayLevelSizesAndCgsBelow
 * @param missingImportantLcgs boolean
 * @returns An object that maps IsoBayNumber to 20's LCG. It doesn't differentiate between ABOVE and BELOW
 */
export default function generate20Lcgs({ maxIsoBay, lcgByBayAbove, lcgByBayBelow, missingImportantLcgs, missingLcgs, blockBaysAndSizesBy20Bay, }) {
    // Return object
    const lcgsBy20Bay = {};
    let minLcgs20 = Infinity;
    let maxLcgs20 = -Infinity;
    const allBlps = [...lcgByBayAbove, ...lcgByBayBelow];
    // Initialize
    for (let iBay = 1; iBay <= maxIsoBay; iBay += 2) {
        const bay = pad3(iBay);
        const block20Info = blockBaysAndSizesBy20Bay[bay];
        const confirmedPairedBay = block20Info === null || block20Info === void 0 ? void 0 : block20Info.allBays.split("-").find((b) => b !== bay);
        lcgsBy20Bay[bay] = {
            maxSize: 20,
            confirmedPairedBay,
            lcgs: new Map(),
            maxForeLcg: -Infinity,
            minAftLcg: Infinity,
        };
    }
    allBlps.forEach((bl) => {
        var _a, _b, _c, _d;
        const maxSize = bl.maxSize || 20;
        const missingLcgsOfBay = missingLcgs[bl.isoBay];
        const objLcgs = getLcgs(missingImportantLcgs, missingLcgsOfBay, (_a = bl.getAvgCGs()) === null || _a === void 0 ? void 0 : _a.perSize);
        if (lcgsBy20Bay[bl.isoBay].paired === undefined)
            lcgsBy20Bay[bl.isoBay].paired = bl.pairedBay;
        if (maxSize > lcgsBy20Bay[bl.isoBay].maxSize)
            lcgsBy20Bay[bl.isoBay].maxSize = maxSize;
        if (objLcgs === null || objLcgs === void 0 ? void 0 : objLcgs.size) {
            objLcgs.forEach((lcgs, size) => {
                var _a, _b;
                (_b = (_a = lcgsBy20Bay[bl.isoBay].lcgs).set) === null || _b === void 0 ? void 0 : _b.call(_a, size, lcgs);
                // Global min and max
                if (lcgs.aftLcg < minLcgs20)
                    minLcgs20 = lcgs.aftLcg;
                if (lcgs.foreLcg > maxLcgs20)
                    maxLcgs20 = lcgs.foreLcg;
                // Local min and max
                if (lcgs.aftLcg < lcgsBy20Bay[bl.isoBay].minAftLcg)
                    lcgsBy20Bay[bl.isoBay].minAftLcg = lcgs.aftLcg;
                if (lcgs.foreLcg > lcgsBy20Bay[bl.isoBay].maxForeLcg)
                    lcgsBy20Bay[bl.isoBay].maxForeLcg = lcgs.foreLcg;
            });
            if (!lcgsBy20Bay[bl.isoBay].lcgs.has(20) && bl.pairedBay !== undefined) {
                const dif20 = feetToMillimeters((maxSize - 20) * 0.5) * (bl.pairedBay === ForeAftEnum.FWD ? -1 : 1);
                const lcgMaxSize = (_b = lcgsBy20Bay[bl.isoBay].lcgs.get(maxSize)) === null || _b === void 0 ? void 0 : _b.lcg;
                if (lcgMaxSize) {
                    (_d = (_c = lcgsBy20Bay[bl.isoBay].lcgs).set) === null || _d === void 0 ? void 0 : _d.call(_c, 20, {
                        lcg: lcgMaxSize + dif20,
                        aftLcg: lcgMaxSize + dif20 - feetToMillimeters(10),
                        foreLcg: lcgMaxSize + dif20 + feetToMillimeters(10),
                    });
                }
                //
            }
        }
    });
    // Is there a 40 bay at the end that is not paired or it is paired with AFT?
    // If so, we need to create a 40 bay paired with FWD
    let finalMaxIsoBay = maxIsoBay;
    const blpsEnd = allBlps.filter((bl) => bl.isoBay === pad3(maxIsoBay));
    if (blpsEnd.some((bl) => bl.maxSize >= 40 && bl.pairedBay !== ForeAftEnum.FWD)) {
        finalMaxIsoBay += 2;
        lcgsBy20Bay[pad3(maxIsoBay)].paired = ForeAftEnum.AFT;
        lcgsBy20Bay[pad3(maxIsoBay)].confirmedPairedBay = pad3(finalMaxIsoBay);
        lcgsBy20Bay[pad3(finalMaxIsoBay)] = {
            maxSize: lcgsBy20Bay[pad3(maxIsoBay)].maxSize || 40,
            paired: ForeAftEnum.FWD,
            confirmedPairedBay: pad3(maxIsoBay),
            lcgs: copy40Lcgs(lcgsBy20Bay[pad3(maxIsoBay)].lcgs),
            maxForeLcg: lcgsBy20Bay[pad3(maxIsoBay)].maxForeLcg,
            minAftLcg: lcgsBy20Bay[pad3(maxIsoBay)].minAftLcg,
        };
    }
    // If there aren't slots defined we need to complete the 20CGs using the paired bay
    for (let iBay = 1; iBay <= finalMaxIsoBay; iBay += 2) {
        const bay = pad3(iBay);
        const b = lcgsBy20Bay[bay];
        if (b.confirmedPairedBay && b.lcgs.size === 0) {
            const pairedBayLcgs = lcgsBy20Bay[b.confirmedPairedBay];
            if (pairedBayLcgs) {
                b.maxForeLcg = pairedBayLcgs.maxForeLcg;
                b.minAftLcg = pairedBayLcgs.minAftLcg;
            }
        }
        else if (b.lcgs.size === 0) {
            const candFwd = lcgsBy20Bay[pad3(iBay - 2)];
            const candAft = lcgsBy20Bay[pad3(iBay + 2)];
            if ((candFwd === null || candFwd === void 0 ? void 0 : candFwd.paired) === ForeAftEnum.AFT) {
                b.paired = ForeAftEnum.FWD;
                b.confirmedPairedBay = pad3(iBay - 2);
                const pairedBayLcgs = lcgsBy20Bay[b.confirmedPairedBay];
                if (pairedBayLcgs) {
                    pairedBayLcgs.confirmedPairedBay = pad3(iBay);
                    b.maxForeLcg = pairedBayLcgs.maxForeLcg;
                    b.minAftLcg = pairedBayLcgs.minAftLcg;
                }
            }
            else if ((candAft === null || candAft === void 0 ? void 0 : candAft.paired) === ForeAftEnum.FWD) {
                b.paired = ForeAftEnum.AFT;
                b.confirmedPairedBay = pad3(iBay + 2);
                const pairedBayLcgs = lcgsBy20Bay[b.confirmedPairedBay];
                if (pairedBayLcgs) {
                    pairedBayLcgs.confirmedPairedBay = pad3(iBay);
                    b.maxForeLcg = pairedBayLcgs.maxForeLcg;
                    b.minAftLcg = pairedBayLcgs.minAftLcg;
                }
            }
        }
    }
    return { lcgsBy20Bay, minLcgs20, maxLcgs20 };
}
function getLcgs(missingImportantLcgs, missingLcgsOfBay, avgCellCgs) {
    if (missingImportantLcgs)
        return missingLcgsOfBay;
    if (!avgCellCgs)
        return undefined;
    const lcgs = new Map();
    avgCellCgs.forEach((cgs, size) => {
        if (cgs.lcg !== undefined) {
            const halfSizeInMM = feetToMillimeters(size / 2);
            lcgs.set(size, {
                lcg: cgs.lcg,
                aftLcg: cgs.lcg - halfSizeInMM,
                foreLcg: cgs.lcg + halfSizeInMM,
            });
        }
    });
    return lcgs;
}
function copy40Lcgs(src) {
    const lcgs = new Map();
    src.forEach((lcg, size) => {
        if (size >= 40)
            lcgs.set(size, Object.assign({}, lcg));
    });
    return lcgs;
}
